import { url } from '../api';
import { HTTP_CONFIG, HTTP_ERROR_CODES } from './httpConfig';
import authClient from '../services/authClient';
import HttpError from './HttpError';

// ========================================
// WEB DUAL AUTHENTICATION HTTP CLIENT
// Cookie-based authentication with CSRF protection
// Per DUAL_AUTH_IMPLEMENTATION.md specification
// Features: Timeout, Retry with exponential backoff, Error handling
// ========================================

const isDev = typeof window !== 'undefined' && process.env.NODE_ENV !== 'production';

// For web apps, we use cookie-based auth (not bearer tokens)
// Tokens are stored in httpOnly cookies by the backend
let refreshInFlight = null;
let lastRefreshTime = 0;
// Prevent refresh spam: minimum time between successful refresh attempts.
// Increase to 5 minutes to avoid frequent backend calls when many 401s occur.
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
// When refresh fails, avoid retrying for a cooldown period to prevent tight retry loops.
const FAILED_REFRESH_COOLDOWN_MS = 5 * 60 * 1000; // fallback cooldown (ms)
// Track consecutive refresh failures to apply exponential backoff
let lastRefreshFailureTime = 0;
let refreshFailureCount = 0;
// Margin before token expiry to avoid refreshing too eagerly (ms)
const REFRESH_TTL_MARGIN_MS = parseInt(process.env.REACT_APP_REFRESH_TTL_MARGIN_MS, 10) || 30 * 1000;
// Backoff parameters for refresh attempts (base and max)
const REFRESH_BACKOFF_BASE_MS = 60 * 1000; // 1 minute
const REFRESH_BACKOFF_MAX_MS = 30 * 60 * 1000; // 30 minutes

// ========================================
// LOGGING UTILITIES (Production-safe)
// ========================================

function logDebug(...args) {
  if (isDev) {
    console.log('[HTTP]', ...args);
  }
}

function logWarn(...args) {
  if (isDev) {
    console.warn('[HTTP]', ...args);
  }
}

function logError(...args) {
  // Always log errors, but sanitize in production
  if (isDev) {
    console.error('[HTTP]', ...args);
  } else {
    // In production, log minimal info without sensitive data
    console.error('[HTTP Error]', args[0]);
  }
}

// ========================================
// CSRF TOKEN MANAGEMENT
// ========================================

/**
 * Get CSRF token from sessionStorage or cookie (read-only, doesn't fetch)
 * @returns {string|null}
 */
function getStoredCsrfToken() {
  // Try sessionStorage first (set by authService.getCsrfToken())
  const stored = sessionStorage.getItem('csrfToken');
  if (stored) return stored;
  
  // Fallback to reading from cookie
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; csrf=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  } catch {}
  return null;
}

// ========================================
// RETRY LOGIC WITH EXPONENTIAL BACKOFF
// ========================================

/**
 * Calculate delay for retry attempt with exponential backoff
 * @param {number} attempt - Current attempt number (0-indexed)
 * @returns {number} Delay in milliseconds
 */
function getRetryDelay(attempt) {
  const { BASE_DELAY_MS, MAX_DELAY_MS } = HTTP_CONFIG.RETRY;
  const delay = BASE_DELAY_MS * Math.pow(2, attempt);
  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 100;
  return Math.min(delay + jitter, MAX_DELAY_MS);
}

/**
 * Check if request should be retried based on status and method
 * @param {number} status - HTTP status code
 * @param {string} method - HTTP method
 * @returns {boolean}
 */
function shouldRetry(status, method) {
  const { RETRYABLE_STATUS_CODES, RETRYABLE_METHODS } = HTTP_CONFIG.RETRY;
  return RETRYABLE_STATUS_CODES.includes(status) && RETRYABLE_METHODS.includes(method.toUpperCase());
}

/**
 * Sleep for specified milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ========================================
// CORE FETCH WITH TIMEOUT
// ========================================

/**
 * Fetch with timeout using AbortController
 * @param {string} fullUrl - Full URL to fetch
 * @param {Object} fetchOptions - Fetch options
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {AbortSignal} externalSignal - Optional external abort signal
 * @returns {Promise<Response>}
 */
async function fetchWithTimeout(fullUrl, fetchOptions, timeoutMs, externalSignal = null) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Link external signal if provided
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', () => controller.abort());
    }
  }

  try {
    const response = await fetch(fullUrl, {
      ...fetchOptions,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ========================================
// MAIN FETCH IMPLEMENTATION
// ========================================

/**
 * Core fetch function with all features:
 * - Timeout with AbortController
 * - Exponential backoff retry for GET requests
 * - CSRF token injection
 * - Session refresh on 401
 * - CSRF token refresh on 403
 * 
 * @param {string} path - API path
 * @param {Object} options - Request options
 * @param {Object} config - Configuration overrides
 * @returns {Promise<Response>}
 */
async function doFetch(path, options = {}, config = {}) {
  const {
    retry = true,
    retryCount = 0,
    maxRetries = HTTP_CONFIG.RETRY.MAX_ATTEMPTS,
    timeout = HTTP_CONFIG.DEFAULT_TIMEOUT_MS,
    signal = null,
  } = config;

  const method = (options.method || 'GET').toUpperCase();
  const fullUrl = url(path);

  // Log API calls in development only
  logDebug(method, path, options.body ? '(with body)' : '');

  // LAST-RESORT GUARD: prevent authenticated API calls when no user is present.
  // This uses localStorage as the single-source for session presence (AuthContext writes `user` there).
  // We allow a small whitelist of public endpoints that must work without authentication.
  try {
    const publicPrefixes = [
      '/api/auth', 
      '/api/health', 
      '/api/public', 
      '/api/indian-exams', 
      '/api/papers', 
      '/api/exams', 
      '/api/guest',
      '/api/silent-mentor',
      '/api/notifications'
    ];
    const hasUser = Boolean(localStorage.getItem('user'));
    const isApiPath = path && path.startsWith('/api/');
    const isPublic = publicPrefixes.some(p => path.startsWith(p));
    if (isApiPath && !isPublic && !hasUser) {
      // Block call to authenticated endpoints when no user is present to avoid zombie requests
      const err = HttpError.unauthenticated(path, method);
      logWarn('Blocked API call while no authenticated user present:', method, path);
      throw err;
    }
  } catch (e) {
    // If storage access fails, proceed conservatively (don't block) — but log in dev
    logWarn('User presence guard check failed', e);
  }

  // Build headers
  const headers = new Headers(options.headers || {});
  // Add client-type header for server-side client detection (mobile/web)
  try {
    const clientType = process.env.REACT_APP_CLIENT_TYPE || localStorage.getItem('clientType');
    if (clientType && !headers.has('X-Client-Type')) {
      headers.set('X-Client-Type', clientType);
    }
  } catch (e) {}
  
  // Don't set Content-Type if body is FormData (browser will set it with boundary)
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  
  // If we have an in-memory access token (Bearer fallback), attach it.
  // Prefer cookie + CSRF flow for web; access token is optional fallback for APIs.
  try {
    const accessToken = authClient.getAccessToken();
    if (accessToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
  } catch (e) {
    // ignore
  }

  // For web clients without Bearer tokens, add CSRF token to ALL requests
  if (!headers.has('X-CSRF-Token')) {
    const csrf = getStoredCsrfToken();
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }

  const fetchOptions = {
    method,
    headers,
    body: options.body,
    cache: 'no-store',
    credentials: 'include', // Always include cookies for web
  };

  let response;

  try {
    response = await fetchWithTimeout(fullUrl, fetchOptions, timeout, signal);
  } catch (error) {
    // Handle timeout/abort errors
    if (error.name === 'AbortError') {
      const httpError = HttpError.timeout(path, method, timeout);
      
      // Retry on timeout for idempotent methods
      if (shouldRetry(408, method) && retryCount < maxRetries) {
        const delay = getRetryDelay(retryCount);
        logWarn(`Request timeout, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
        await sleep(delay);
        return doFetch(path, options, { ...config, retryCount: retryCount + 1 });
      }
      
      throw httpError;
    }
    
    // Handle network errors
    const httpError = HttpError.networkError(path, method, error);
    
    // Retry network errors for idempotent methods
    if (httpError.isRetryable && shouldRetry(0, method) && retryCount < maxRetries) {
      const delay = getRetryDelay(retryCount);
      logWarn(`Network error, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
      await sleep(delay);
      return doFetch(path, options, { ...config, retryCount: retryCount + 1 });
    }
    
    throw httpError;
  }

  // Handle 401 (session expired) - attempt refresh with TTL check and exponential backoff
  if (response.status === 401 && retry) {
    const now = Date.now();

    // If we have a short-lived in-memory access token and it's not expiring soon,
    // skip cookie refresh attempts (prevents unnecessary refresh traffic).
    try {
      if (typeof authClient.isAccessTokenExpired === 'function') {
        const tokenExpired = authClient.isAccessTokenExpired(REFRESH_TTL_MARGIN_MS);
        if (!tokenExpired) {
          logDebug('Access token still valid (within TTL margin); skipping refresh');
          return response;
        }
      }
    } catch (e) {
      // ignore errors from authClient probe
    }

    // If there are recorded refresh failures, use exponential backoff before trying again
    if (refreshFailureCount > 0 && lastRefreshFailureTime) {
      const backoff = Math.min(REFRESH_BACKOFF_BASE_MS * Math.pow(2, refreshFailureCount - 1), REFRESH_BACKOFF_MAX_MS);
      const sinceFailure = now - lastRefreshFailureTime;
      if (sinceFailure < backoff) {
        logWarn('Skipping refresh due to exponential backoff', { backoff, sinceFailure, refreshFailureCount });
        try {
          window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { reason: 'session_expired' } }));
        } catch {}
        return response;
      }
    }

    // Fallback static cooldown guard (keeps previous behavior if env vars not set)
    const timeSinceLastFailedRefresh = now - lastRefreshFailureTime;
    if (timeSinceLastFailedRefresh < FAILED_REFRESH_COOLDOWN_MS) {
      logWarn('Recent refresh failure; skipping refresh attempt for cooldown period');
      try {
        window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { reason: 'session_expired' } }));
      } catch {}
      return response;
    }

    // If we refreshed recently and still getting 401, don't retry - session is likely invalid
    const timeSinceLastRefresh = now - lastRefreshTime;
    if (timeSinceLastRefresh < REFRESH_COOLDOWN_MS) {
      logWarn('401 received shortly after refresh - session invalid, dispatching unauthorized');
      try {
        window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { reason: 'session_expired' } }));
      } catch {}
      return response;
    }

    try {
      if (!refreshInFlight) {
        refreshInFlight = refreshSession();
      }
      await refreshInFlight;

      // Successful refresh: record time and reset failure counters
      lastRefreshTime = Date.now();
      refreshFailureCount = 0;
      lastRefreshFailureTime = 0;

      // Retry original request after refresh
      return doFetch(path, options, { ...config, retry: false });
    } catch (refreshError) {
      // Record failure timestamp and increment failure counter
      refreshFailureCount = Math.min(refreshFailureCount + 1, 32);
      lastRefreshFailureTime = Date.now();
      try {
        window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { reason: 'session_expired' } }));
      } catch {}
      return response; // Return 401 to caller
    } finally {
      refreshInFlight = null;
    }
  }

  // Handle 403 CSRF mismatch - get new CSRF and retry
  if (response.status === 403 && retry) {
    try {
      const errorData = await response.clone().json();
      if (errorData.error?.code === 'CSRF_MISMATCH' || errorData.code === 'CSRF_MISMATCH') {
        // Import authService to fetch new CSRF token from server
        const { getCsrfToken } = await import('../services/authService');
        await getCsrfToken();
        
        // Retry with new CSRF token
        return doFetch(path, options, { ...config, retry: false });
      }
    } catch (e) {
      // If parsing fails, just return the response
    }
  }

  // Handle 429 Rate Limiting
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After');
    logWarn('Rate limited', retryAfter ? `Retry-After: ${retryAfter}s` : '');
    
    // Dispatch event for UI to show rate limit warning
    try {
      window.dispatchEvent(new CustomEvent('http:rate-limited', {
        detail: { retryAfter: retryAfter ? parseInt(retryAfter, 10) : 60 }
      }));
    } catch {}
  }

  // Retry on server errors for GET requests
  if (shouldRetry(response.status, method) && retryCount < maxRetries) {
    const delay = getRetryDelay(retryCount);
    logWarn(`Server error ${response.status}, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
    await sleep(delay);
    return doFetch(path, options, { ...config, retryCount: retryCount + 1 });
  }

  return response;
}

// ========================================
// SESSION REFRESH
// ========================================

async function refreshSession() {
  // For web clients, prefer cookie-based refresh. Server should set HttpOnly refresh cookie.
  logDebug('Trying cookie-based refresh...');
  const csrf = getStoredCsrfToken();
  const { endpoints } = await import('../api');

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HTTP_CONFIG.DEFAULT_TIMEOUT_MS);

  try {
    const res = await fetch(url(endpoints.auth.refreshSession), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrf || '',
      },
      credentials: 'include',
      signal: controller.signal,
    });

    if (!res.ok) {
      throw new Error('Session refresh failed');
    }

    // If server returned a Bearer access token as convenience, store in-memory
    try {
      const refreshData = await res.clone().json();
      if (refreshData.accessToken) {
        try {
          const { setAccessToken } = await import('../services/authClient');
          setAccessToken(refreshData.accessToken, refreshData.expiresIn);
        } catch (e) {
          // ignore
        }
      }
      // Do NOT persist refresh tokens to localStorage. Server should manage HttpOnly cookie.
    } catch (e) {
      // Ignore JSON parse errors
    }

    logDebug('Cookie-based refresh successful');
    try { window.dispatchEvent(new CustomEvent('auth:updated', { detail: { sessionRefreshed: true } })); } catch {}
    return { ok: true };
  } finally {
    clearTimeout(timeoutId);
  }
}

// ========================================
// PUBLIC API
// ========================================

// In-flight GET request dedupe map to avoid duplicate identical GET requests
const inFlightGetRequests = new Map();


/**
 * HTTP GET request with retry support
 * @param {string} path - API path
 * @param {Object} options - Additional options (headers, timeout, signal)
 * @returns {Promise<Response>}
 */
export async function httpGet(path, options = {}) {
  const { headers, timeout, signal, ...rest } = options;

  // Use full URL + stable options as dedupe key. Exclude non-serializable fields like `signal`.
  let fullUrl = path;
  try { fullUrl = url(path); } catch (e) {}
  const dedupeKey = `${fullUrl}|${JSON.stringify(rest || {})}`;

  if (inFlightGetRequests.has(dedupeKey)) {
    return inFlightGetRequests.get(dedupeKey);
  }

  const promise = (async () => {
    try {
      const res = await doFetch(path, { method: 'GET', headers, ...rest }, { timeout, signal });

      // Read body once into ArrayBuffer so multiple callers can parse independently
      let bodyBuffer = null;
      try {
        bodyBuffer = await res.arrayBuffer();
      } catch (e) {
        // If body can't be read (e.g., no content), leave as null
        bodyBuffer = null;
      }

      // Store response metadata to create fresh responses for each caller
      const responseData = {
        status: res.status,
        statusText: res.statusText,
        headers: new Headers(res.headers),
        bodyBuffer
      };

      // Return factory function that creates fresh Response for each caller
      return {
        ...responseData,
        // Create a method that returns a fresh Response each time
        json: async () => {
          if (!bodyBuffer) return null;
          const text = new TextDecoder().decode(bodyBuffer);
          return JSON.parse(text);
        },
        text: async () => {
          if (!bodyBuffer) return '';
          return new TextDecoder().decode(bodyBuffer);
        },
        arrayBuffer: async () => bodyBuffer ? bodyBuffer.slice(0) : new ArrayBuffer(0),
        blob: async () => bodyBuffer ? new Blob([bodyBuffer]) : new Blob(),
        clone: () => {
          const init = {
            status: responseData.status,
            statusText: responseData.statusText,
            headers: new Headers(responseData.headers),
          };
          return new Response(bodyBuffer ? bodyBuffer.slice(0) : undefined, init);
        },
        ok: res.ok
      };
    } finally {
      inFlightGetRequests.delete(dedupeKey);
    }
  })();

  inFlightGetRequests.set(dedupeKey, promise);
  return promise;
}

/**
 * HTTP POST request
 * @param {string} path - API path
 * @param {*} body - Request body (will be JSON stringified unless it's FormData)
 * @param {Object} options - Additional options (headers, timeout, signal)
 * @returns {Promise<Response>}
 */
export async function httpPost(path, body, options = {}) {
  const { headers, timeout, signal, ...rest } = options;
  
  // Check if body is FormData - if so, don't stringify and don't set Content-Type
  if (body instanceof FormData) {
    const customHeaders = new Headers(headers || {});
    // Remove Content-Type if present - browser will set it with correct boundary
    customHeaders.delete('Content-Type');
    return doFetch(
      path, 
      { method: 'POST', body, headers: customHeaders, ...rest }, 
      { timeout, signal }
    );
  }
  
  return doFetch(path, { method: 'POST', body: JSON.stringify(body), headers, ...rest }, { timeout, signal });
}

/**
 * HTTP DELETE request
 * @param {string} path - API path
 * @param {*} body - Optional request body
 * @param {Object} options - Additional options
 * @returns {Promise<Response>}
 */
export async function httpDelete(path, body, options = {}) {
  const { headers, timeout, signal, ...rest } = options;
  return doFetch(
    path,
    { method: 'DELETE', body: body ? JSON.stringify(body) : undefined, headers, ...rest },
    { timeout, signal }
  );
}

/**
 * HTTP PUT request
 * @param {string} path - API path
 * @param {*} body - Request body
 * @param {Object} options - Additional options
 * @returns {Promise<Response>}
 */
export async function httpPut(path, body, options = {}) {
  const { headers, timeout, signal, ...rest } = options;
  return doFetch(path, { method: 'PUT', body: JSON.stringify(body), headers, ...rest }, { timeout, signal });
}

/**
 * HTTP PATCH request
 * @param {string} path - API path
 * @param {*} body - Request body
 * @param {Object} options - Additional options
 * @returns {Promise<Response>}
 */
export async function httpPatch(path, body, options = {}) {
  const { headers, timeout, signal, ...rest } = options;
  return doFetch(path, { method: 'PATCH', body: JSON.stringify(body), headers, ...rest }, { timeout, signal });
}

// ========================================
// UTILITY EXPORTS
// ========================================

export { getStoredCsrfToken, HttpError, HTTP_CONFIG, HTTP_ERROR_CODES };
